import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api, { AUTH_LOGOUT_EVENT } from '../api/axios';
import { Usuario, LoginRequest, LoginResponse } from '../types';
import { STORAGE_KEYS } from '../utils/constants';

interface AuthContextData {
  usuario: Usuario | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  hasPermission: (perfis: string[]) => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar usuario do localStorage ao iniciar
  useEffect(() => {
    const loadStoredUser = () => {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      if (storedUser && storedToken) {
        setUsuario(JSON.parse(storedUser));
      }

      setIsLoading(false);
    };

    loadStoredUser();
  }, []);

  // Escutar evento de logout forçado (token expirado)
  useEffect(() => {
    const handleForcedLogout = () => {
      setUsuario(null);
    };

    window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    return () => {
      window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    };
  }, []);

  // Funcao de login
  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    const { usuario, accessToken, refreshToken } = response.data;

    // Salvar no localStorage
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(usuario));

    setUsuario(usuario);
  }, []);

  // Funcao de logout
  const logout = useCallback(() => {
    // Chamar API de logout (opcional)
    api.post('/auth/logout').catch(() => {
      // Ignorar erro de logout
    });

    // Limpar localStorage
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);

    setUsuario(null);
  }, []);

  // Verificar permissao
  const hasPermission = useCallback((perfis: string[]) => {
    if (!usuario) return false;
    return perfis.includes(usuario.perfil);
  }, [usuario]);

  return (
    <AuthContext.Provider
      value={{
        usuario,
        isAuthenticated: !!usuario,
        isLoading,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar o contexto
export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}

export default AuthContext;
